import { isNumber } from "lodash";

import {
  Action,
  ActionBase,
  ActionType,
  BallActionBase,
  BallMovement,
  BasketballAction,
  BasketballActionType,
  BasketballMiscType,
  FrameStencil,
  Marker,
  MarkerBase,
  MarkerType,
  MovementType,
  Point,
  PointType,
  StencilType,
} from "../../../../types";
import { convertRadiansToDegrees } from "../../../../utils";
import {
  ActionHandoffStencil,
  ActionPassStencil,
  ActionStencil,
  ActionStencilName,
  FrameDTO,
  HashpassStencilName,
  MarkerBallStencil,
  MarkerStencil,
  MarkerStencilName,
  MotionType,
  Stencil,
  StencilMidPoint,
  StencilMidPointType,
  actionStencilNames,
  markerStencilNames,
} from "../types";

/** ================================
 * Get Action Type
 ================================ */
interface GetActionTypeProps {
  stencilName: ActionStencilName;
  motionType: MotionType;
}
const getActionType = ({ stencilName, motionType }: GetActionTypeProps): ActionType => {
  switch (stencilName) {
    case "StraightCutStencil":
    case "CurvedCutStencil":
    case "AngledCutStencil":
    case "AngleCurveCutStencil":
    case "CurveAngleCutStencil":
    case "DoubleCurvedCutStencil":
    case "AngleDoubleCurveCutStencil":
    case "StraightScreenStencil":
    case "CurvedScreenStencil":
    case "AngledScreenStencil":
    case "AngleCurveScreenStencil":
    case "CurveAngleScreenStencil":
    case "DoubleCurvedScreenStencil":
    case "AngleDoubleCurveScreenStencil":
      return ActionType.PLAYER;

    case "StraightPassStencil":
    case "CurvedPassStencil":
    case "AngledPassStencil":
    case "AngleCurvePassStencil":
    case "CurveAnglePassStencil":
    case "DoubleCurvedPassStencil":
    case "AngleDoubleCurvePassStencil":
    case "StraightHashpass":
    case "HashPass1Stencil":
    case "HashPass2Stencil":
    case "HashPass3Stencil":
    case "HashPass4Stencil":
    case "HashPass5Stencil":
    case "CurvedHashpass":
    case "HashPass1CurveStencil":
    case "HashPass2CurveStencil":
    case "HashPass3CurveStencil":
    case "HashPass4CurveStencil":
    case "HashPass5CurveStencil":
      return ActionType.BALL;

    case "HandoffStencil":
      return ActionType.HANDOFF;

    case "StraightDribbleStencil":
    case "CurvedDribbleStencil":
    case "AngledDribbleStencil":
    case "AngleCurveDribbleStencil":
    case "CurveAngleDribbleStencil":
    case "DoubleCurvedDribbleStencil":
    case "AngleDoubleCurveDribbleStencil":
      return ActionType.POSSESSION;

    case "MultiSegmentStencil": {
      switch (motionType) {
        case MotionType.PASS:
          return ActionType.BALL;
        case MotionType.DRIBBLE:
        case MotionType.CUT_RECEIVING_HANDOFF:
        case MotionType.SCREEN_RECEIVING_HANDOFF:
          return ActionType.POSSESSION;
        case MotionType.CUT:
        case MotionType.SCREEN:
        default:
          return ActionType.PLAYER;
      }
    }
  }
};

/** ================================
 * Get Basketball Action Type
 ================================ */
interface GetBasketballActionTypeProps {
  stencilName: ActionStencilName;
  motionType: MotionType;
}
const getBasketballActionType = ({ stencilName, motionType }: GetBasketballActionTypeProps): BasketballActionType => {
  switch (stencilName) {
    case "StraightCutStencil":
    case "CurvedCutStencil":
    case "AngledCutStencil":
    case "AngleCurveCutStencil":
    case "CurveAngleCutStencil":
    case "DoubleCurvedCutStencil":
    case "AngleDoubleCurveCutStencil":
      return BasketballActionType.CUT;

    case "StraightScreenStencil":
    case "CurvedScreenStencil":
    case "AngledScreenStencil":
    case "AngleCurveScreenStencil":
    case "CurveAngleScreenStencil":
    case "DoubleCurvedScreenStencil":
    case "AngleDoubleCurveScreenStencil":
      return BasketballActionType.SCREEN;

    case "StraightPassStencil":
    case "CurvedPassStencil":
    case "AngledPassStencil":
    case "AngleCurvePassStencil":
    case "CurveAnglePassStencil":
    case "DoubleCurvedPassStencil":
    case "AngleDoubleCurvePassStencil":
      return BasketballActionType.PASS;

    case "StraightHashpass":
    case "HashPass1Stencil":
    case "HashPass2Stencil":
    case "HashPass3Stencil":
    case "HashPass4Stencil":
    case "HashPass5Stencil":
    case "CurvedHashpass":
    case "HashPass1CurveStencil":
    case "HashPass2CurveStencil":
    case "HashPass3CurveStencil":
    case "HashPass4CurveStencil":
    case "HashPass5CurveStencil":
      return BasketballActionType.HASHPASS;

    case "HandoffStencil":
      return BasketballActionType.HANDOFF;

    case "StraightDribbleStencil":
    case "CurvedDribbleStencil":
    case "AngledDribbleStencil":
    case "AngleCurveDribbleStencil":
    case "CurveAngleDribbleStencil":
    case "DoubleCurvedDribbleStencil":
    case "AngleDoubleCurveDribbleStencil":
      return BasketballActionType.DRIBBLE;

    case "MultiSegmentStencil": {
      switch (motionType) {
        case MotionType.SCREEN:
          return BasketballActionType.SCREEN;
        case MotionType.PASS:
          return BasketballActionType.PASS;
        case MotionType.DRIBBLE:
          return BasketballActionType.DRIBBLE;
        case MotionType.CUT_RECEIVING_HANDOFF:
          return BasketballActionType.CUT_RECEIVING_HANDOFF;
        case MotionType.SCREEN_RECEIVING_HANDOFF:
          return BasketballActionType.SCREEN_RECEIVING_HANDOFF;
        case MotionType.CUT:
        default:
          return BasketballActionType.CUT;
      }
    }
  }
};

/** ================================
 * Get Position
 ================================ */
// Get position from stencil, returning first pos point if it is a shading
const getPosition = (markerStencil: MarkerStencil): Point => {
  switch (markerStencil.stencilName) {
    case "BasketballPlayerStencil":
    case "PossessionStencil":
    case "HoopStencil":
    case "BallStencil":
    case "FlagStencil":
    case "ConeStencil":
    case "FreeTextStencil":
      return markerStencil.point || { x: 0, y: 0 };

    case "RectangleShadingStencil":
    case "TrapezoidShadingStencil":
      return {
        x: Math.min(...(markerStencil?.point?.map(({ x }) => x) || [])),
        y: Math.min(...(markerStencil?.point?.map(({ y }) => y) || [])),
      };
    case "OvalShadingStencil":
      return {
        x: markerStencil.point?.[0]?.x || 0,
        y: markerStencil.point?.[0]?.y || 0,
      };
  }
};

/** ================================
 * Get Mid Points
 ================================ */
// get point type based on stencil type, and point position
const getPointType = (type: StencilMidPointType): PointType => {
  switch (type) {
    case "angle":
      return PointType.ANGLE;

    case "curve":
      return PointType.CURVE;
  }
};
const getMidPoints = (midPoints: StencilMidPoint[] | undefined = []): Action["midPoints"] => {
  return midPoints.map((point) => ({ x: point.x, y: point.y, pointType: getPointType(point.type) })) || [];
};

/** ================================
 * Get Number of Hashes
 ================================ */
const getNumberOfHashes = (stencil: HashpassStencilName): number => {
  switch (stencil) {
    case "StraightHashpass":
    case "CurvedHashpass":
    case "HashPass1Stencil":
    case "HashPass1CurveStencil":
      return 1;

    case "HashPass2Stencil":
    case "HashPass2CurveStencil":
      return 2;

    case "HashPass3Stencil":
    case "HashPass3CurveStencil":
      return 3;

    case "HashPass4Stencil":
    case "HashPass4CurveStencil":
      return 4;

    case "HashPass5Stencil":
    case "HashPass5CurveStencil":
      return 5;
  }
};

export const isMarkerStencil = (stencil: Stencil): stencil is MarkerStencil =>
  markerStencilNames.includes(stencil.stencilName as MarkerStencilName);

export const isActionStencil = (stencil: Stencil): stencil is ActionStencil =>
  actionStencilNames.includes(stencil.stencilName as ActionStencilName);

/** ================================
 * Convert DB Stencil to Frame Stencil
 ================================ */
export const convertBBDBStencilToFrameStencil = (frame: FrameDTO): FrameStencil[] | null => {
  // if d3Stencils is not stringified data, return null
  const d3Stencils = frame.d3Stencils;
  if (d3Stencils === null || d3Stencils === "") return null;

  // parse stencil data
  const stencilData: Stencil[] = JSON.parse(d3Stencils);

  if (!Array.isArray(stencilData)) return null;

  const actionStencils: ActionStencil[] = stencilData.filter((stencil): stencil is ActionStencil =>
    isActionStencil(stencil)
  );
  const markerStencils: MarkerStencil[] = stencilData.filter((stencil): stencil is MarkerStencil =>
    isMarkerStencil(stencil)
  );

  /** ======== Actions ======== */
  const actions: BasketballAction[] = actionStencils.map((dbAction) => {
    const { id, stencilName, motionType, start, end, trueEnd, midPoints, color, theta, animation } = dbAction;

    // define ActionBase object
    const actionBase: ActionBase = {
      id,
      stencilType: StencilType.ACTION,
      pos: start,
      endPos: trueEnd || end,
      ...(trueEnd && end && trueEnd.x !== end.x && trueEnd.y !== end.y && { classicEndPoint: end }),
      midPoints: getMidPoints(midPoints),
      color: color || "#000",
      rotate: (theta && convertRadiansToDegrees(theta)) || undefined,
      startTime: animation?.moveStart && isNumber(animation?.moveStart) ? animation.moveStart : 0,
      endTime: animation?.moveEnd && isNumber(animation?.moveEnd) ? animation.moveEnd : 1000,
    };

    const actionType = getActionType({ stencilName, motionType });
    const basketballActionType = getBasketballActionType({ stencilName, motionType });

    return ((): BasketballAction => {
      switch (actionType) {
        case ActionType.PLAYER: {
          switch (basketballActionType) {
            case BasketballActionType.CUT:
            case BasketballActionType.SCREEN:
              return {
                ...actionBase,
                actionType,
                layerType: basketballActionType,
                nextPlayerAction: dbAction.nextPlayerAction || null,
              };
            default:
              throw new Error(`Invalid Action and Layer Types ${actionType} - ${basketballActionType}}`);
          }
        }

        case ActionType.BALL: {
          const basePass: Omit<
            BallActionBase<BasketballActionType.PASS | BasketballActionType.HASHPASS>,
            "layerType"
          > = {
            ...actionBase,
            actionType,
            nextBallAction: dbAction.nextBallAction || null,
            nextPlayer: (dbAction as ActionPassStencil)?.nextPlayer || null,
            prevPlayer: (dbAction as ActionPassStencil)?.prevPlayer || null,
            playerActionStart: dbAction.playerActionStart || null,
            playerActionEnd: dbAction.playerActionEnd || null,
          };
          switch (basketballActionType) {
            case BasketballActionType.HASHPASS: {
              return {
                ...basePass,
                layerType: basketballActionType,
                numberOfHashes: getNumberOfHashes(stencilName as HashpassStencilName),
              };
            }
            case BasketballActionType.PASS: {
              const pass = { ...basePass, layerType: basketballActionType };
              // find prevPlayer and nextPlayer on pass
              // find pass in ballMovements
              const ballMovementOnPass = markerStencils
                .filter((s): s is MarkerBallStencil => s.stencilName === "PossessionStencil")
                .find((s) => s.movements?.some((m) => m.id === basePass.id));
              if (!ballMovementOnPass) return pass;

              // get all ballMovements
              const ballMovements = ballMovementOnPass.movements;
              if (!ballMovements) return pass;

              // get index of pass in ballMovements
              const passIndex = ballMovements.findIndex((m) => m.id === basePass.id);
              // ball movement before pass
              const prevMovement = ballMovements[passIndex - 1];
              // ball movement after pass
              const nextMovement = ballMovements[passIndex + 1];
              const prevPlayer =
                prevMovement && prevMovement?.movementType === MovementType.POSSESSION ? prevMovement.id : null;
              const nextPlayer =
                nextMovement && nextMovement?.movementType === MovementType.POSSESSION ? nextMovement.id : null;

              return { ...pass, prevPlayer, nextPlayer };
            }
            default:
              throw new Error(`Invalid Action and Layer Types ${actionType} - ${basketballActionType}}`);
          }
        }

        case ActionType.HANDOFF: {
          return {
            ...actionBase,
            actionType: ActionType.HANDOFF,
            layerType: BasketballActionType.HANDOFF,
            startPos: (dbAction as ActionHandoffStencil).startPos || actionBase.pos,
            prevPlayer: (dbAction as ActionHandoffStencil).prevPlayer || null,
            nextPlayer: (dbAction as ActionHandoffStencil).nextPlayer || null,
            prevAction: (dbAction as ActionHandoffStencil).prevAction || null,
            nextAction: (dbAction as ActionHandoffStencil).nextAction || null,
          };
        }

        case ActionType.POSSESSION: {
          switch (basketballActionType) {
            case BasketballActionType.DRIBBLE:
            case BasketballActionType.CUT_RECEIVING_HANDOFF:
            case BasketballActionType.SCREEN_RECEIVING_HANDOFF:
              return {
                ...actionBase,
                actionType,
                layerType: basketballActionType,
                nextPlayerAction: dbAction.nextPlayerAction || null,
                nextBallAction: dbAction.nextBallAction || null,
              };
            default:
              throw new Error(`Invalid Action and Layer Types ${actionType} - ${basketballActionType}}`);
          }
        }
      }
    })();
  });

  /** ======== Markers ======== */
  const markers: Marker[] = markerStencils
    .map((marker): Marker | null => {
      const markerBase: MarkerBase = {
        id: `${marker.id}`,
        stencilName: marker.stencilName,
        stencilType: StencilType.MARKER,
        label: marker.text,
        color: marker.color || undefined,
        rotate: (marker.theta && convertRadiansToDegrees(marker.theta)) || undefined,
        pos: getPosition(marker),
      };

      switch (marker.stencilName) {
        case "BasketballPlayerStencil": {
          return {
            ...markerBase,
            markerType: MarkerType.PLAYER,
            fill: marker.type === "fill",
            possession: marker.playerWithBall,
            playerIdNumber: marker.playerIdNumber || 1,
            movements: marker?.movements || [],
          };
        }

        case "PossessionStencil": {
          // find first movement stencil
          const stencilFromFirstMovement = markerStencils.find((s) => s.id === marker.movements?.[0]?.id);
          // associatedPlayer is first movement, assuming it is a player stencil
          const associatedPlayer =
            stencilFromFirstMovement?.stencilName === "BasketballPlayerStencil" ? stencilFromFirstMovement.id : "";
          // convert movements to BallMovement[]
          const movements: BallMovement[] =
            marker.movements?.reduce<BallMovement[]>((acc, movement, i): BallMovement[] => {
              const movementStencil = stencilData.find((s) => s.id === movement.id);
              // if no stencil data, return null
              if (!movementStencil) return acc;

              const isPlayer = movementStencil.stencilName === "BasketballPlayerStencil";
              const isBallAction =
                isActionStencil(movementStencil) &&
                getActionType({ stencilName: movementStencil.stencilName, motionType: movementStencil.motionType });
              // if stencil is not a player or ball action stencil, return null
              if (!isPlayer && !isBallAction) return acc;

              // if stencil is a player stencil..
              if (isPlayer) {
                // if first movement, return movement with startTime = 0
                if (i === 0)
                  return [
                    ...acc,
                    { id: movement.id, movementType: MovementType.POSSESSION, startTime: movement.startTime || 0 },
                  ];
                // else, get prior movement.
                else {
                  const priorMovement = acc[i - 1];
                  if (!priorMovement) return acc;

                  // if prior movement is a player stencil, return player's startTime + 1.
                  if (priorMovement.movementType === MovementType.POSSESSION)
                    return [
                      ...acc,
                      { ...movement, movementType: MovementType.POSSESSION, startTime: priorMovement.startTime + 1 },
                    ];

                  // if prior movement is a ball action, return ball action end time.
                  if (priorMovement.movementType === MovementType.BALL_ACTION) {
                    const priorMovementStencil = actionStencils.find((s) => s.id === priorMovement.id);
                    if (!priorMovementStencil) return acc;
                    return [
                      ...acc,
                      {
                        ...movement,
                        movementType: MovementType.POSSESSION,
                        startTime: movement.startTime || priorMovementStencil.animation?.moveEnd || 0,
                      },
                    ];
                  }

                  return acc;
                }
              }
              // if stencil is a ball action stencil, add to movemetns
              else {
                return [...acc, { ...movement, movementType: MovementType.BALL_ACTION }];
              }
            }, []) || [];

          return { ...markerBase, associatedPlayer, markerType: MarkerType.BALL, movements };
        }

        case "HoopStencil":
          return { ...markerBase, markerType: MarkerType.MISC, miscType: BasketballMiscType.HOOP };
        case "BallStencil":
          return { ...markerBase, markerType: MarkerType.MISC, miscType: BasketballMiscType.BALL_STATIC };
        case "FlagStencil":
          return { ...markerBase, markerType: MarkerType.MISC, miscType: BasketballMiscType.FLAG };
        case "ConeStencil":
          return { ...markerBase, markerType: MarkerType.MISC, miscType: BasketballMiscType.CONE };
        case "FreeTextStencil":
          return { ...markerBase, markerType: MarkerType.TEXT, width: Number(marker.width) };

        case "RectangleShadingStencil": {
          const minX = Math.min(...(marker?.point?.map(({ x }) => x) || []));
          const minY = Math.min(...(marker?.point?.map(({ y }) => y) || []));
          const maxX = Math.max(...(marker?.point?.map(({ x }) => x) || []));
          const maxY = Math.max(...(marker?.point?.map(({ y }) => y) || []));
          const width = maxX - minX;
          const height = maxY - minY;

          return { ...markerBase, markerType: MarkerType.SHADING_RECTANGLE, width, height };
        }
        case "OvalShadingStencil": {
          const points = marker.points || marker.point.map((p) => [p.x, p.y]);
          const minX = Math.min(...points.map(([x]) => x));
          const maxX = Math.max(...points.map(([x]) => x));
          const minY = Math.min(...points.map(([_x, y]) => y));
          const maxY = Math.max(...points.map(([_x, y]) => y));

          const rx = (maxX - minX) / 2;
          const ry = (maxY - minY) / 2;

          return { ...markerBase, markerType: MarkerType.SHADING_OVAL, rx, ry };
        }
        case "TrapezoidShadingStencil":
          return { ...markerBase, markerType: MarkerType.SHADING_TRAPEZOID, shadeVertices: marker?.point || [] };
      }
    })
    .filter((marker): marker is Marker => marker !== undefined && marker !== null);

  // sort markers so ball type is always first
  const markersSorted = [...markers].sort((a, b) => {
    if (a.markerType === MarkerType.BALL) return -1;
    if (b.markerType === MarkerType.BALL) return 1;
    return 0;
  });
  return [...actions, ...markersSorted];
};
